import React, { useState, useEffect } from 'react';
import { Avatar, Button, Link, Grid, IconButton, Rating, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { styled } from '@mui/material/styles';
import { deleteObject, ref, getMetadata } from 'firebase/storage';
import { storage, db } from '../service/Firebase-config';
import { updateDoc, doc, getDoc } from 'firebase/firestore';
import { stringAvatar } from '../common/util';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DeleteConfirmation from './DeleteConfirmation';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const HomeworkUploads = () => {
  const { selectedUser, setTotalStars } = useGlobalContext();
  const { selectedHw, rating, setRating } = useHomeworkContext();

  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  const homeworkRef = doc(db, `students/${selectedUser.uid}/homeworks/${selectedHw?.id}`);

  useEffect(() => {
    if (selectedHw) {
      setFileList(selectedHw.fileStudent || []);
    }
  }, [selectedHw]);

  useEffect(() => {
    const patchMissingCreatedDate = async () => {
      if (!selectedHw?.id || !selectedUser?.uid || !fileList?.length) return;

      const hasMissingDate = fileList.some((file) => !file.createdDate);
      if (!hasMissingDate) return;

      setIsLoading(true);

      try {
        const updatedFileList = await Promise.all(
          fileList.map(async (file) => {
            if (file.createdDate) return file;

            try {
              const studentFileRef = ref(
                storage,
                `file/${selectedUser.uid}/${selectedHw.id}/${file.savedName}`
              );

              const metadata = await getMetadata(studentFileRef);

              return {
                ...file,
                createdDate: metadata.timeCreated || null,
              };
            } catch (error) {
              console.error('Error loading metadata:', error);
              return file;
            }
          })
        );

        const changed = updatedFileList.some(
          (file, index) => !fileList[index]?.createdDate && file.createdDate
        );

        if (changed) {
          setFileList(updatedFileList);
          await updateDoc(homeworkRef, { fileStudent: updatedFileList });
        }
      } catch (error) {
        console.error('Error patching file createdDate:', error);
      } finally {
        setIsLoading(false);
      }
    };

    patchMissingCreatedDate();
  }, [fileList, selectedHw, selectedUser, homeworkRef]);

  const handleRemoveFile = async (file) => {
    setIsLoading(true);
    const studentFileRef = ref(storage, `file/${selectedUser.uid}/${selectedHw.id}/${file.savedName}`);
    try {
      await deleteObject(studentFileRef);
      const updatedFileList = fileList.filter((item) => item.savedName !== file.savedName);
      setFileList(updatedFileList);
      await updateDoc(homeworkRef, { fileStudent: updatedFileList });
    } catch (error) {
      console.error('Error removing file:', error);
    } finally {
      setSelectedFile(null);
      setIsLoading(false);
    }
  };

  const handleUpdateRating = async (newValue) => {
    const studentRef = doc(db, 'students', selectedUser.uid);
    const studentSnapshot = await getDoc(studentRef);

    if (studentSnapshot.exists()) {
      const studentData = studentSnapshot.data();
      const currentTotalStars = studentData.totalStars || 0;

      const homeworkSnapshot = await getDoc(homeworkRef);
      if (homeworkSnapshot.exists()) {
        const homeworkData = homeworkSnapshot.data();
        const currentRating = homeworkData.rating || 0;
        const newTotalStars = currentTotalStars + newValue - currentRating;

        await updateDoc(homeworkRef, { rating: newValue });
        await updateDoc(studentRef, { totalStars: newTotalStars });

        setRating(newValue);
        setTotalStars(newTotalStars);
      }
    }
  };

  const handleClearRating = () => {
    handleUpdateRating(0);
  };

  return (
    <React.Fragment>
      <Grid container spacing={1}>
        <Grid item xs={12} md={5} lg={6}>
          <Grid container direction="column" alignItems="center" gap={1}>
            <Typography>{selectedUser.displayName}</Typography>
            <Avatar
              src={selectedUser.photoUrl || undefined}
              {...(!selectedUser.photoUrl && stringAvatar(selectedUser.displayName))}
              sx={{ width: 60, height: 60 }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Rating
                name="simple-controlled"
                value={rating}
                onChange={(event, newValue) => handleUpdateRating(newValue)}
                size="large"
              />
              <Button onClick={handleClearRating} variant="outlined">
                Clear
              </Button>
            </div>
          </Grid>
        </Grid>

        <Grid item xs={12} md={7} lg={6} container direction="column" alignItems="center" gap={1}>
          <Button disabled component="label" variant="contained" startIcon={<CloudUploadIcon />}>
            Files from Student
            <VisuallyHiddenInput type="file" />
          </Button>

          {fileList &&
            fileList.map((file) => (
             <Grid
                key={file.savedName}
                item
                width="80%"
                p={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  '&:hover': { backgroundColor: '#e3f2fd' },
                }}
              >
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                  <Typography>
                    <Link href={file.fileUrl} target="_blank">
                      {file.fileName}
                    </Link>
                  </Typography>

                  <IconButton color="inherit" size="small" onClick={() => setSelectedFile(file)}>
                    <DeleteRoundedIcon />
                  </IconButton>
                </div>

                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color: 'text.secondary',
                  }}
                >
                  {file.createdDate
                    ? dayjs(file.createdDate).format('YYYY-MM-DD hh:mm A')
                    : 'No upload date'}
                </Typography>
              </Grid>
            ))}

          {isLoading && <Typography variant="h5">Loading ...</Typography>}
        </Grid>
      </Grid>

      <DeleteConfirmation
        open={Boolean(selectedFile)}
        closeDelete={() => setSelectedFile(null)}
        handleDelete={() => handleRemoveFile(selectedFile)}
        title="Are you sure you want to delete this file?"
      />
    </React.Fragment>
  );
};

export default HomeworkUploads;